import { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FilterOptions } from "../types";
import { Filter, X, Search } from "lucide-react";

interface FilterPanelProps {
  filters: FilterOptions;
  onFilterChange: (filters: FilterOptions) => void;
  availableRegions: { value: string; label: string; }[];
  availableSites: string[];
  availableCustomers: string[];
}

export const FilterPanel = ({ filters, onFilterChange, availableRegions, availableSites, availableCustomers }: FilterPanelProps) => {
  const [searchQueries, setSearchQueries] = useState({
    region: '',
    siteName: '',
    customer: ''
  });
  const [showSuggestions, setShowSuggestions] = useState({
    region: false,
    siteName: false,
    customer: false
  });

  const hasActiveFilters = filters.region !== '' && filters.region !== 'all' || 
                       filters.siteName !== '' && filters.siteName !== 'all' || 
                       filters.customer !== '' && filters.customer !== 'all';

  const getFilteredSuggestions = (type: 'region' | 'siteName' | 'customer', query: string): any[] => {
    if (type === 'region') {
      const filteredRegions = !query.trim() 
        ? availableRegions.slice(0, 10) 
        : availableRegions.filter(region => region.label.toLowerCase().includes(query.toLowerCase())).slice(0, 10);
      return filteredRegions;
    }
    
    const availableItems = type === 'siteName' ? availableSites : availableCustomers;
    
    if (!query.trim()) return availableItems.slice(0, 10); // Show first 10 when no search
    
    return availableItems
      .filter(item => item.toLowerCase().includes(query.toLowerCase()))
      .slice(0, 10); // Limit to 10 suggestions
  };

  const handleSearchChange = (type: 'region' | 'siteName' | 'customer', value: string) => {
    setSearchQueries(prev => ({ ...prev, [type]: value }));
    setShowSuggestions(prev => ({ ...prev, [type]: true }));
  };

  const handleSuggestionClick = (type: 'region' | 'siteName' | 'customer', value: string) => {
    onFilterChange({ ...filters, [type]: value });
    setSearchQueries(prev => ({ ...prev, [type]: value }));
    setShowSuggestions(prev => ({ ...prev, [type]: false }));
  };

  const handleClearSearch = (type: 'region' | 'siteName' | 'customer') => {
    setSearchQueries(prev => ({ ...prev, [type]: '' }));
    onFilterChange({ ...filters, [type]: 'all' });
    setShowSuggestions(prev => ({ ...prev, [type]: false }));
  };

  const clearFilters = () => {
    onFilterChange({ region: 'all', siteName: 'all', customer: 'all' });
    setSearchQueries({ region: '', siteName: '', customer: '' });
    setShowSuggestions({ region: false, siteName: false, customer: false });
  };

  const updateFilter = (key: keyof FilterOptions, value: string) => {
    onFilterChange({ ...filters, [key]: value });
  };

  return (
    <Card className="p-6 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">Filters</h3>
        </div>
        {hasActiveFilters && (
          <Button 
            onClick={clearFilters} 
            variant="outline" 
            size="sm"
            className="text-xs"
          >
            <X className="w-3 h-3 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Region Filter with Search */}
        <div className="space-y-2 relative">
          <Label htmlFor="region-search">Region</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="region-search"
              placeholder="Search regions..."
              value={searchQueries.region}
              onChange={(e) => handleSearchChange('region', e.target.value)}
              onFocus={() => setShowSuggestions(prev => ({ ...prev, region: true }))}
              onBlur={() => setTimeout(() => setShowSuggestions(prev => ({ ...prev, region: false })), 200)}
              className="pl-10 pr-10"
            />
            {searchQueries.region && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleClearSearch('region')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-muted"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          {showSuggestions.region && (
            <div className="absolute top-full left-0 right-0 z-50 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
              <div 
                className="p-2 hover:bg-muted cursor-pointer text-sm border-b"
                onClick={() => handleSuggestionClick('region', 'all')}
              >
                All Regions
              </div>
              {(getFilteredSuggestions('region', searchQueries.region) as { value: string; label: string; }[]).map((regionObj) => (
                <div
                  key={regionObj.value}
                  className="p-2 hover:bg-muted cursor-pointer text-sm"
                  onClick={() => handleSuggestionClick('region', regionObj.value)}
                >
                  {regionObj.label}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Site Name Filter with Search */}
        <div className="space-y-2 relative">
          <Label htmlFor="site-search">Site Name</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="site-search"
              placeholder="Search sites..."
              value={searchQueries.siteName}
              onChange={(e) => handleSearchChange('siteName', e.target.value)}
              onFocus={() => setShowSuggestions(prev => ({ ...prev, siteName: true }))}
              onBlur={() => setTimeout(() => setShowSuggestions(prev => ({ ...prev, siteName: false })), 200)}
              className="pl-10 pr-10"
            />
            {searchQueries.siteName && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleClearSearch('siteName')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-muted"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          {showSuggestions.siteName && (
            <div className="absolute top-full left-0 right-0 z-50 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
              <div 
                className="p-2 hover:bg-muted cursor-pointer text-sm border-b"
                onClick={() => handleSuggestionClick('siteName', 'all')}
              >
                All Sites
              </div>
              {(getFilteredSuggestions('siteName', searchQueries.siteName) as string[]).map((site) => (
                <div
                  key={site}
                  className="p-2 hover:bg-muted cursor-pointer text-sm"
                  onClick={() => handleSuggestionClick('siteName', site)}
                >
                  {site}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Customer Filter with Search */}
        <div className="space-y-2 relative">
          <Label htmlFor="customer-search">Customer</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="customer-search"
              placeholder="Search customers..."
              value={searchQueries.customer}
              onChange={(e) => handleSearchChange('customer', e.target.value)}
              onFocus={() => setShowSuggestions(prev => ({ ...prev, customer: true }))}
              onBlur={() => setTimeout(() => setShowSuggestions(prev => ({ ...prev, customer: false })), 200)}
              className="pl-10 pr-10"
            />
            {searchQueries.customer && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleClearSearch('customer')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-muted"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          {showSuggestions.customer && (
            <div className="absolute top-full left-0 right-0 z-50 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
              <div 
                className="p-2 hover:bg-muted cursor-pointer text-sm border-b"
                onClick={() => handleSuggestionClick('customer', 'all')}
              >
                All Customers
              </div>
              {(getFilteredSuggestions('customer', searchQueries.customer) as string[]).map((customer) => (
                <div
                  key={customer}
                  className="p-2 hover:bg-muted cursor-pointer text-sm"
                  onClick={() => handleSuggestionClick('customer', customer)}
                >
                  {customer}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {hasActiveFilters && (
        <div className="mt-4 flex flex-wrap gap-2">
          {filters.region && filters.region !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
              Region: {availableRegions.find(r => r.value === filters.region)?.label || filters.region}
              <button
                onClick={() => updateFilter('region', 'all')}
                className="hover:bg-primary/20 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.siteName && filters.siteName !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-secondary/10 text-secondary text-xs rounded-full">
              Site: {filters.siteName}
              <button
                onClick={() => updateFilter('siteName', 'all')}
                className="hover:bg-secondary/20 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.customer && filters.customer !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs rounded-full">
              Customer: {filters.customer}
              <button
                onClick={() => updateFilter('customer', 'all')}
                className="hover:bg-success/20 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      )}
    </Card>
  );
};